import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetBulkTransferComponentsEligibility } from '../../../hooks/api/components';
import { useAuth } from '../../../hooks/auth/useAuth';
import useDialogContext from '../../../hooks/dialog/useDialogContext';
import { PCFBiogenicEmissionsMode } from '../../../shared/enums/pcf';
import { IComponent } from '../../../shared/interfaces/IComponent';

type BulkComponentActionsButtonProps = {
  biogenicEmissionsMode: PCFBiogenicEmissionsMode;
  componentsToShowPendingDuringBulk: IComponent[];
  setComponentsToShowPendingDuringBulk: (products: IComponent[]) => void;
};

// The rendered menu item's DOM id doubles as the tooltip target, since PrimeReact's
// Menu assigns `item.id` straight to the underlying <li>.
const BULK_TRANSFER_MENU_ITEM_ID = 'request-bulk-transfer-components-dialog';

const BulkComponentActionsButton = ({
  biogenicEmissionsMode,
  componentsToShowPendingDuringBulk,
  setComponentsToShowPendingDuringBulk,
}: BulkComponentActionsButtonProps): JSX.Element | null => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();
  const buttonLabel = t('componentsPage.bulkActions');

  const menuRef = useRef<Menu>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<Tooltip>(null);
  const [syncedWidth, setSyncedWidth] = useState<number>();
  const {
    data: bulkTransferEligibility,
    isError: isBulkTransferEligibilityError,
    isFetching: isBulkTransferEligibilityLoading,
    refetch: getBulkTransferEligibility,
  } = useGetBulkTransferComponentsEligibility();

  const isBulkTransferDisabled =
    isBulkTransferEligibilityLoading ||
    isBulkTransferEligibilityError ||
    bulkTransferEligibility?.isEligible !== true;

  useEffect(() => {
    // Reset width on label change (e.g language switch) to allow natural resizing.
    setSyncedWidth(undefined);
  }, [buttonLabel]);

  useEffect(() => {
    const handleResize = () => {
      // Reset synced width on resize to allow components to resize naturally
      setSyncedWidth(undefined);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      return window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (user?.company?.license === 'basic') {
    return null;
  }

  /**
   * Calculates a single width that will be shared by both
   * the Button and the popup Menu
   */
  const syncWidths = () => {
    const buttonWidth = buttonRef.current?.offsetWidth ?? 0;
    requestAnimationFrame(() => {
      const menuRoot = menuRef.current?.getElement();
      const menuWidth = menuRoot?.getBoundingClientRect().width ?? 0;

      setSyncedWidth(Math.max(buttonWidth, menuWidth));

      // The popup menu unmounts its items on close, so the disabled item's
      // element is re-created fresh every time it opens. PrimeReact's Tooltip
      // only resolves its target selector once, when it first mounts, so it
      // has to be told to rebind explicitly on every open.
      tooltipRef.current?.updateTargetEvents();
    });
  };

  const onMenuShow = () => {
    syncWidths();

    if (user?.company?.license === 'reseller') {
      void getBulkTransferEligibility();
    }
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    menuRef?.current?.toggle(event);
  };

  const bulkTransferMenuItemTemplate = (item: MenuItem, options: MenuItemOptions) => {
    return (
      <div className='p-menuitem-content' data-pc-section='content'>
        <a
          href='#'
          className={`${options.className} justify-content-between`}
          tabIndex={-1}
          aria-label={item.label}
          aria-hidden='true'
          aria-disabled={item.disabled}
          data-p-disabled={item.disabled}
          onClick={options.onClick}
        >
          <span className={classNames(options.labelClassName, 'mr-2')}>{item.label}</span>
          <span
            className='ml-auto flex justify-content-center'
            style={{ width: '1rem', minWidth: '1rem' }}
            aria-hidden='true'
          >
            <i
              className='pi pi-spin pi-spinner'
              style={{ visibility: isBulkTransferEligibilityLoading ? 'visible' : 'hidden' }}
            />
          </span>
        </a>
      </div>
    );
  };

  const menuItems: MenuItem[] = [
    {
      id: 'export-pcfs-dialog',
      label: t('componentsPage.pcfExport'),
      command: () => {
        return openDialog('export-pcfs-dialog', { biogenicEmissionsMode });
      },
    },
    {
      id: 'bulk-request-pcfs-dialog',
      label: t('componentsPage.pcfRequest'),
      command: () => {
        return openDialog('bulk-request-pcfs-dialog', {
          componentsToShowPendingDuringBulk,
          setComponentsToShowPendingDuringBulk,
        });
      },
    },
  ];

  if (user?.company?.license === 'reseller') {
    menuItems.unshift({
      id: BULK_TRANSFER_MENU_ITEM_ID,
      label: t('componentsPage.transferComponent'),
      disabled: isBulkTransferDisabled,
      template: bulkTransferMenuItemTemplate,
      command: () => {
        return openDialog('request-bulk-transfer-components-dialog');
      },
    });
  }

  return (
    <>
      {!isBulkTransferEligibilityLoading && bulkTransferEligibility?.isEligible === false && (
        <Tooltip
          ref={tooltipRef}
          target={`#${BULK_TRANSFER_MENU_ITEM_ID}`}
          position='right'
          content={t('componentsPage.transferComponentDisabledTooltip')}
          className='pointer-events-auto'
        />
      )}

      <Menu
        ref={menuRef}
        popup
        model={menuItems}
        onShow={onMenuShow}
        style={{ width: syncedWidth ? `${syncedWidth}px` : 'auto' }}
      />

      <Button
        label={buttonLabel}
        onClick={onClick}
        style={{ width: syncedWidth ? `${syncedWidth}px` : 'auto' }}
        pt={{
          root: {
            ref: buttonRef,
          },
        }}
      />
    </>
  );
};

export default BulkComponentActionsButton;
